import { Badge } from './Badge';
import { IconLock } from './Icon';
import { formatMoney } from '@/shared/types/Money';
import type { LinePriceSource } from '@/shared/pricing/types';

// Where a document line's price came from (rep-app program section 4): a
// customer special price ("Contract"), a price list ("List: Wholesale A"), the
// item's list price ("Base", "Base -10%" with the customer's default
// discount), a typed price with nothing to compare against ("Manual") or a
// typed price that replaced the resolved one ("Manual" too, with a lock and the
// warning tone; owner's request 2026-10-22). Small
// badge for dense line tables; the tooltip carries the list price, the typed
// price and the reason.
export type PriceSourceBadgeProps = {
  source: LinePriceSource | null | undefined;
  priceListName?: string | null;
  discountPercent?: string | number | null;
  listUnitPrice?: string | number | null;
  submittedUnitPrice?: string | number | null;
  overrideReason?: string | null;
  currency?: string;
};

const TONE: Record<LinePriceSource, 'default' | 'success' | 'warning' | 'info'> = {
  contract: 'success',
  customer_list: 'info',
  default_list: 'info',
  base: 'default',
  manual: 'default',
  override: 'warning',
};

const WHAT: Record<LinePriceSource, string> = {
  contract: 'Special price for this customer',
  customer_list: "From the customer's price list",
  default_list: 'From the company default price list',
  base: "The item's list price",
  manual: 'Price entered by hand',
  override: 'Price changed by hand',
};

// "-10%" for a positive discount (trailing zeros dropped), else null.
function discountSuffix(v: string | number | null | undefined): string | null {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `-${Number(n.toFixed(4))}%`;
}

export function priceSourceLabel(p: PriceSourceBadgeProps): string {
  const off = discountSuffix(p.discountPercent);
  switch (p.source) {
    case 'contract': return 'Contract';
    case 'customer_list':
    case 'default_list': {
      const list = p.priceListName ? `List: ${p.priceListName}` : 'List';
      return off ? `${list} ${off}` : list;
    }
    case 'base': return off ? `Base ${off}` : 'Base';
    case 'manual': return 'Manual';
    // A typed price that replaced the resolved one reads Manual too (owner's
    // request 2026-10-22, the Sales app's badge); its tone and tooltip still
    // tell it from a line nothing priced.
    case 'override': return 'Manual';
    default: return '';
  }
}

export function priceSourceTooltip(p: PriceSourceBadgeProps): string {
  if (!p.source) return '';
  const ccy = p.currency || 'USD';
  const has = (v: string | number | null | undefined): v is string | number => v != null && v !== '';
  const lines = [WHAT[p.source]];
  if (has(p.listUnitPrice)) lines.push(`List price ${formatMoney(p.listUnitPrice, ccy)}`);
  if (has(p.submittedUnitPrice)) lines.push(`Typed price ${formatMoney(p.submittedUnitPrice, ccy)}`);
  if (p.overrideReason) lines.push(`Reason: ${p.overrideReason}`);
  return lines.join('\n');
}

export function PriceSourceBadge(props: PriceSourceBadgeProps) {
  if (!props.source) return <span className="text-muted">—</span>;
  const label = priceSourceLabel(props);
  return (
    <span title={priceSourceTooltip(props)} className="inline-flex whitespace-nowrap">
      <Badge tone={TONE[props.source]}>
        {props.source === 'override'
          ? <span className="inline-flex items-center gap-1"><IconLock size={11} aria-hidden="true" />{label}</span>
          : label}
      </Badge>
    </span>
  );
}
